import { Link } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaClock, FaMoneyBillWave, FaReceipt, FaUser } from "react-icons/fa";
import PageHeader from "src/components/pageHeader";
import { SlotRequest, SlotRequestStatus } from "../types/slot-request.entity";

type Props = {
  request: SlotRequest & { id: string };
  onBack: () => void;
};

const statusClass = (status: SlotRequestStatus) => {
  switch (status) {
    case "PENDING":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "ACCEPTED":
      return "border-emerald-200 bg-emerald-100 text-emerald-700";
    case "REJECTED":
      return "border-rose-200 bg-rose-100 text-rose-700";
    case "EXPIRED":
      return "border-slate-200 bg-slate-100 text-slate-700";
    case "CANCELLED":
      return "border-red-200 bg-red-100 text-red-700";
  }
};

const formatDateTime = (value: any) => {
  const date = value?.toDate?.() ?? (value instanceof Date ? value : null);
  if (!date) return "-";
  return date.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" });
};

const formatCurrency = (currency: string) => (currency === "KARMA" ? "K" : currency);

const personLinkClass = "inline-flex max-w-full items-center rounded border px-3 py-2 font-mono text-xs font-semibold no-underline transition-colors";

export const SlotRequestDetails = ({ request, onBack }: Props) => {
  const amountDetails = request.money.amountDetails;

  return (
    <div className="page_container">
      <PageHeader text="Slot Request Details" />

      <button
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
      >
        <FaArrowLeft />
        Back to requests
      </button>

      <section className="mb-6 border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Request ID</p>
            <p className="mt-1 break-all font-mono text-sm text-slate-800">{request.id}</p>
          </div>
          <span className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold ${statusClass(request.status)}`}>
            {request.status}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaUser className="text-emerald-600" /> Learner</div>
            <Link to={`/users/${request.learnerId}`} className={`${personLinkClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
              {request.learnerId}
            </Link>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaUser className="text-blue-600" /> Coach</div>
            <Link to={`/users/${request.coachId}`} className={`${personLinkClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}>
              {request.coachId}
            </Link>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaCalendarAlt className="text-violet-600" /> Requested for</div>
            <p className="text-sm text-slate-800">{formatDateTime(request.requestedDate)}</p>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaClock className="text-slate-500" /> Created</div>
            <p className="text-sm text-slate-800">{formatDateTime(request.createdAt)}</p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800"><FaMoneyBillWave className="text-emerald-600" /> Reserved balance</div>
          <p className="text-2xl font-semibold text-slate-900">
            {request.money.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-base text-slate-500">{formatCurrency(request.money.currency)}</span>
          </p>
          <p className="mt-1 text-xs text-slate-500">Amount captured when the request was created.</p>

          {amountDetails && (
            <dl className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3 border-t border-slate-100 pt-4 text-sm">
              {amountDetails.totalAmount != null && <><dt className="text-slate-500">Total</dt><dd className="text-right font-medium text-slate-800">{amountDetails.totalAmount}</dd></>}
              {amountDetails.coachAmount != null && <><dt className="text-slate-500">Coach amount</dt><dd className="text-right font-medium text-slate-800">{amountDetails.coachAmount}</dd></>}
              {amountDetails.businessFee != null && <><dt className="text-slate-500">Business fee</dt><dd className="text-right font-medium text-slate-800">{amountDetails.businessFee}</dd></>}
              {amountDetails.vatAmount != null && <><dt className="text-slate-500">VAT</dt><dd className="text-right font-medium text-slate-800">{amountDetails.vatAmount}</dd></>}
            </dl>
          )}
        </section>

        <section className="border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800"><FaReceipt className="text-sky-600" /> Request content</div>
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Note</p>
              <p className="whitespace-pre-wrap text-sm text-slate-800">{request.note || "No note provided."}</p>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Motivations</p>
              {request.motivations?.length ? (
                <div className="flex flex-wrap gap-2">
                  {request.motivations.map((motivation) => (
                    <span key={motivation} className="border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700">
                      {motivation.replaceAll("_", " ")}
                    </span>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500">No motivations provided.</p>}
            </div>
          </div>
        </section>

        <section className="border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 text-base font-semibold text-slate-800">Linked records</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Learner transaction</p>
              <p className="break-all font-mono text-xs text-slate-800">{request.learnerTransactionId}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Booking</p>
              {request.bookingId ? <Link to={`/bookings/${request.bookingId}`} className="font-mono text-xs text-emerald-700 no-underline hover:underline">{request.bookingId}</Link> : <p className="text-sm text-slate-500">Not created</p>}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Slot</p>
              {request.slotId ? <Link to={`/slots/${request.slotId}`} className="font-mono text-xs text-sky-700 no-underline hover:underline">{request.slotId}</Link> : <p className="text-sm text-slate-500">Not created</p>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SlotRequestDetails;
